import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Button,
  Code,
  FormControl,
  FormHelperText,
  FormLabel,
  Box,
  HStack,
  Input,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import axios from 'axios'
import { PhoneCall } from 'lucide-react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  type VoiceAiCallPayload,
  type PhoneLineType,
  applyAvailabilityToPayload,
  applyPhoneToPayload,
  formatAvailabilityLabel,
  formatVoiceAiPhone,
  hasVoiceAiPhone,
  inferPhoneLineType,
  isoToDatetimeLocal,
  validateAvailabilityRange,
  validatePhoneDigits,
} from '@/lib/voiceAi/buildVoiceAiCallPayload'
import { useTicketOptions } from '@/hooks/useTicketOptions'

export type {
  VoiceAiCallPayload,
  VoiceAiQuestionnaireItem,
  VoiceAiPhone,
  PhoneLineType,
  VoiceAiAvailability,
} from '@/lib/voiceAi/buildVoiceAiCallPayload'

const ALICE_WEBHOOK_URL =
  import.meta.env.VITE_ALICE_WEBHOOK_URL ?? 'http://127.0.0.1:8000/api/webhooks/alice-echo'

type ModalStep = 'configure' | 'calling' | 'result'

type Props = {
  isOpen: boolean
  onClose: () => void
  payload: VoiceAiCallPayload | null
  onSubmitted?: () => void
}

export function AliceCallOutConfirmModal({ isOpen, onClose, payload, onSubmitted }: Props) {
  const { showSuccess, showError } = useToastNotification()
  const { options: phoneLineTypeOptions } = useTicketOptions('phone_line_type')
  const [step, setStep] = useState<ModalStep>('configure')
  const [responsePreview, setResponsePreview] = useState('')
  const [responseError, setResponseError] = useState<string | null>(null)
  const [countryCode, setCountryCode] = useState('63')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneLineType, setPhoneLineType] = useState<PhoneLineType>('mobile')
  const [phoneInputError, setPhoneInputError] = useState<string | null>(null)
  const [availabilityFrom, setAvailabilityFrom] = useState('')
  const [availabilityUntil, setAvailabilityUntil] = useState('')
  const [availabilityError, setAvailabilityError] = useState<string | null>(null)

  const payloadRef = useRef<VoiceAiCallPayload | null>(payload)
  payloadRef.current = payload
  const onSubmittedRef = useRef(onSubmitted)
  onSubmittedRef.current = onSubmitted

  const resetForOpen = useCallback(() => {
    const current = payloadRef.current
    setResponsePreview('')
    setResponseError(null)
    setPhoneInputError(null)
    setAvailabilityError(null)
    setStep('configure')
    if (!current) {
      setCountryCode('63')
      setPhoneNumber('')
      setPhoneLineType('mobile')
      setAvailabilityFrom('')
      setAvailabilityUntil('')
      return
    }
    const existing = current.contact.phone
    setCountryCode(existing.countryCode || '63')
    setPhoneNumber(existing.number || '')
    setPhoneLineType(inferPhoneLineType(existing.number))
    setAvailabilityFrom(isoToDatetimeLocal(current.availability.from))
    setAvailabilityUntil(isoToDatetimeLocal(current.availability.until))
  }, [])

  const handleCallNow = useCallback(async () => {
    const body = payloadRef.current
    if (!body) return
    if (!hasVoiceAiPhone(body)) {
      setStep('configure')
      setPhoneInputError('Contact phone number is required to call out.')
      return
    }
    setStep('calling')
    setResponseError(null)
    setPhoneInputError(null)
    setAvailabilityError(null)
    try {
      const response = await axios.post(ALICE_WEBHOOK_URL, body)
      setResponsePreview(JSON.stringify(response.data ?? {}, null, 2))
      showSuccess(
        'ALICE call initiated',
        'The ticket will be updated automatically after ALICE completes the call.'
      )
      onSubmittedRef.current?.()
      setStep('result')
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown }; message?: string }
      const errorData = err?.response?.data ?? { message: err?.message ?? 'Request failed' }
      setResponsePreview(JSON.stringify(errorData, null, 2))
      setResponseError(err?.message ?? 'Failed to call ALICE.')
      showError(error)
      setStep('result')
    }
  }, [showSuccess, showError])

  const handleStartCallOut = useCallback(() => {
    const base = payloadRef.current
    if (!base) return

    const code = countryCode.trim()
    const number = phoneNumber.trim()
    if (!code) {
      setPhoneInputError('Country code is required.')
      return
    }
    const phoneValidationError = validatePhoneDigits(phoneLineType, number)
    if (phoneValidationError) {
      setPhoneInputError(phoneValidationError)
      return
    }

    const rangeError = validateAvailabilityRange(availabilityFrom, availabilityUntil)
    if (rangeError) {
      setAvailabilityError(rangeError)
      return
    }

    let updated = applyPhoneToPayload(base, code, number, phoneLineType)
    updated = applyAvailabilityToPayload(updated, availabilityFrom, availabilityUntil)
    payloadRef.current = updated
    setPhoneInputError(null)
    setAvailabilityError(null)
    void handleCallNow()
  }, [
    countryCode,
    phoneNumber,
    phoneLineType,
    availabilityFrom,
    availabilityUntil,
    handleCallNow,
  ])

  useEffect(() => {
    if (!isOpen) return
    resetForOpen()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleClose = () => {
    if (step === 'calling') return
    onClose()
  }

  const contactLabel = payload
    ? [payload.contact.firstName, payload.contact.lastName].filter(Boolean).join(' ') || '–'
    : '–'

  const displayPhone = payloadRef.current?.contact.phone
  const phoneLabel =
    displayPhone && hasVoiceAiPhone(payloadRef.current!) ? formatVoiceAiPhone(displayPhone) : '–'

  const availabilityLabel = payloadRef.current
    ? formatAvailabilityLabel(payloadRef.current.availability)
    : '–'

  const numberPlaceholder = phoneLineType === 'mobile' ? '9171234567' : '28123456'
  const numberHelper =
    phoneLineType === 'mobile'
      ? 'Philippine mobile: 10 digits starting with 9. Do not include +63 or leading 0.'
      : 'Landline: include area code (e.g. 2 for Metro Manila, 32 for Cebu). Digits only.'

  const needsPhone = payload ? !hasVoiceAiPhone(payload) : true

  const headerTitle =
    step === 'configure' ? 'Call out' : step === 'calling' ? 'Calling ALICE' : 'ALICE Webhook Response'

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{headerTitle}</ModalHeader>
        <ModalCloseButton isDisabled={step === 'calling'} />
        <ModalBody>
          {!payload ? (
            <Text fontSize="sm" color="gray.500">
              No payload available.
            </Text>
          ) : step === 'configure' ? (
            <VStack align="stretch" spacing={4}>
              {needsPhone ? (
                <Alert status="warning" borderRadius="md" alignItems="flex-start">
                  <AlertIcon />
                  <Text fontSize="sm" flex="1">
                    This request has no valid phone number on file. Enter the contact number before starting
                    the voice AI call.
                  </Text>
                </Alert>
              ) : null}
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={4}
                py={3}
                bg="gray.50"
              >
                <Text fontSize="xs" fontWeight="semibold" mb={2}>
                  Contact
                </Text>
                <Text fontSize="sm">{contactLabel}</Text>
                {payload.contact.email ? (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Email: {payload.contact.email}
                  </Text>
                ) : null}
              </Box>
              <FormControl as="fieldset" mb={0}>
                <FormLabel as="legend" fontSize="sm" mb={2}>
                  Phone type
                </FormLabel>
                <RadioGroup
                  value={phoneLineType}
                  onChange={(value) => setPhoneLineType(value as PhoneLineType)}
                >
                  <Stack direction="row" spacing={6} align="center">
                    {phoneLineTypeOptions.map((o) => (
                      <Radio key={o.code} value={o.code} size="sm">
                        {o.label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
              <FormControl isRequired isInvalid={!!phoneInputError}>
                <SimpleGrid
                  templateColumns={{ base: '1fr', sm: '140px minmax(0, 1fr)' }}
                  templateRows={{ base: 'auto auto auto auto', sm: 'auto auto' }}
                  columnGap={3}
                  rowGap={2}
                  alignItems="start"
                >
                  <FormLabel
                    fontSize="sm"
                    mb={0}
                    htmlFor="callout-country-code"
                    gridColumn={{ base: 1, sm: 1 }}
                    gridRow={{ base: 1, sm: 1 }}
                    minH={{ sm: '1.25rem' }}
                  >
                    Country code
                  </FormLabel>
                  <FormLabel
                    fontSize="sm"
                    mb={0}
                    htmlFor="callout-phone-number"
                    gridColumn={{ base: 1, sm: 2 }}
                    gridRow={{ base: 3, sm: 1 }}
                    minH={{ sm: '1.25rem' }}
                  >
                    {phoneLineType === 'mobile' ? 'Mobile number' : 'Landline number'}
                  </FormLabel>
                  <Input
                    id="callout-country-code"
                    size="sm"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="63"
                    inputMode="numeric"
                    gridColumn={{ base: 1, sm: 1 }}
                    gridRow={{ base: 2, sm: 2 }}
                  />
                  <Input
                    id="callout-phone-number"
                    size="sm"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder={numberPlaceholder}
                    inputMode="numeric"
                    gridColumn={{ base: 1, sm: 2 }}
                    gridRow={{ base: 4, sm: 2 }}
                  />
                </SimpleGrid>
                {phoneInputError ? (
                  <FormHelperText color="red.500" mt={2}>
                    {phoneInputError}
                  </FormHelperText>
                ) : (
                  <FormHelperText mt={2}>{numberHelper}</FormHelperText>
                )}
              </FormControl>
              <FormControl isInvalid={!!availabilityError}>
                <FormLabel fontSize="sm" mb={2}>
                  Availability (optional)
                </FormLabel>
                <SimpleGrid
                  templateColumns={{ base: '1fr', sm: '1fr 1fr' }}
                  columnGap={3}
                  rowGap={2}
                  alignItems="start"
                >
                  <Box>
                    <FormLabel fontSize="xs" color="gray.600" mb={1} htmlFor="callout-availability-from">
                      From
                    </FormLabel>
                    <Input
                      id="callout-availability-from"
                      type="datetime-local"
                      size="sm"
                      value={availabilityFrom}
                      onChange={(e) => setAvailabilityFrom(e.target.value)}
                    />
                  </Box>
                  <Box>
                    <FormLabel fontSize="xs" color="gray.600" mb={1} htmlFor="callout-availability-until">
                      Until
                    </FormLabel>
                    <Input
                      id="callout-availability-until"
                      type="datetime-local"
                      size="sm"
                      value={availabilityUntil}
                      min={availabilityFrom || undefined}
                      onChange={(e) => setAvailabilityUntil(e.target.value)}
                    />
                  </Box>
                </SimpleGrid>
                {availabilityError ? (
                  <FormHelperText color="red.500" mt={2}>
                    {availabilityError}
                  </FormHelperText>
                ) : (
                  <FormHelperText mt={2}>
                    Leave empty to send null for both. Times use your local timezone and are sent as +08:00 in
                    the payload.
                  </FormHelperText>
                )}
              </FormControl>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              {step === 'calling' ? (
                <HStack spacing={3}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Submitting payload to ALICE webhook...</Text>
                </HStack>
              ) : (
                <>
                  <Alert status={responseError ? 'error' : 'success'} borderRadius="md" alignItems="flex-start">
                    <AlertIcon />
                    <Text fontSize="sm" flex="1">
                      {responseError
                        ? 'Request failed. Showing error response payload.'
                        : 'Request submitted successfully. Showing JSON response payload.'}
                    </Text>
                  </Alert>

                  <Code
                    as="pre"
                    display="block"
                    whiteSpace="pre-wrap"
                    p={3}
                    borderRadius="md"
                    fontSize="xs"
                    maxH="400px"
                    overflowY="auto"
                  >
                    {responsePreview || '{}'}
                  </Code>

                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="xs" fontWeight="semibold">
                      Ticket
                    </Text>
                    <SimpleGrid templateColumns="minmax(100px, 35%) 1fr" gap={2} columnGap={4}>
                      <Text fontSize="xs" color="gray.600">
                        Title
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {payload.ticket.title || '–'}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Request #
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {String(payload.ticket.metadata.requestNumber ?? '–')}
                      </Text>
                    </SimpleGrid>
                    <Text fontSize="xs" fontWeight="semibold" pt={1}>
                      Contact
                    </Text>
                    <SimpleGrid templateColumns="minmax(100px, 35%) 1fr" gap={2} columnGap={4}>
                      <Text fontSize="xs" color="gray.600">
                        Name
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {contactLabel}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Phone
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {phoneLabel}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Email
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {payload.contact.email || '–'}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Availability
                      </Text>
                      <Text fontSize="xs" textAlign="right" noOfLines={3}>
                        {availabilityLabel}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Questions to ask
                      </Text>
                      <Text fontSize="xs" textAlign="right">
                        {payload.questionnaire.length}
                      </Text>
                      {payload.questionnaire.length > 5 ? (
                        <Text fontSize="xs" color="orange.600" gridColumn="1 / -1">
                          Consider adding store and triage details in the request form to shorten
                          the ALICE call.
                        </Text>
                      ) : null}
                      {payload.ticket.metadata.storeCode ? (
                        <>
                          <Text fontSize="xs" color="gray.600">
                            Store
                          </Text>
                          <Text fontSize="xs" textAlign="right">
                            {String(payload.ticket.metadata.storeCode)}
                          </Text>
                        </>
                      ) : null}
                    </SimpleGrid>
                  </VStack>
                </>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {step === 'configure' ? (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button leftIcon={<PhoneCall size={16} />} colorScheme="green" onClick={handleStartCallOut}>
                Continue call out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={step === 'calling'}>
                {step === 'calling' ? 'Please wait...' : 'Close'}
              </Button>
              {step === 'result' && payload && (
                <Button
                  leftIcon={<PhoneCall size={16} />}
                  colorScheme="green"
                  variant="outline"
                  onClick={() => {
                    setStep('configure')
                  }}
                >
                  Retry
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
